import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import Column from 'react-virtualized/dist/commonjs/Table/Column';
import SortDirection from 'react-virtualized/dist/commonjs/Table/SortDirection';
import Table from 'react-virtualized/dist/commonjs/Table';
import InfiniteLoader from 'react-virtualized/dist/commonjs/InfiniteLoader';

import styles from './styles';

class MuiVirtualizedTable extends React.PureComponent {
    getRowClassName = ({ index }) => {
        const { classes, rowClassName, showRowHover } = this.props;

        return classNames(classes.tableRow, classes.flexContainer, rowClassName, {
            [classes.tableRowHover]: index !== -1 && showRowHover,
        });
    };

    cellRenderer = ({ cellData, columnIndex = null }) => {
        const {
            columns, classes, rowHeight, onRowClick, tableCellStyle,
        } = this.props;
        return (
            <TableCell
                component="div"
                className={classNames(classes.tableCell, classes.flexContainer, {
                    [classes.noClick]: onRowClick == null,
                })}
                variant="body"
                style={{ height: rowHeight, ...tableCellStyle }}
                align={(columnIndex != null && columns[columnIndex].numeric) || false ? 'right' : 'left'}
            >
                {cellData}
            </TableCell>
        );
    };

    headerRenderer = ({
        label, columnIndex, dataKey, sortBy, sortDirection,
    }) => {
        const {
            headerHeight, columns, classes, sort, tableCellStyle,
        } = this.props;
        const direction = {
            [SortDirection.ASC]: 'asc',
            [SortDirection.DESC]: 'desc',
        };

        const inner = !columns[columnIndex].disableSort && sort != null ? (
            <TableSortLabel active={dataKey === sortBy} direction={direction[sortDirection]}>
                {label}
            </TableSortLabel>
        ) : (
            label
        );

        return (
            <TableCell
                component="div"
                className={classNames(classes.tableCell, classes.flexContainer, classes.noClick)}
                variant="head"
                style={{ height: headerHeight, ...tableCellStyle }}
                align={columns[columnIndex].numeric || false ? 'right' : 'left'}
            >
                {inner}
            </TableCell>
        );
    };

    render() {
        const {
            classes,
            className: tableClassName,
            columns,
            isRowLoaded,
            loadMoreRows,
            infiniteLoaderRowCount,
            ...tableProps
        } = this.props;
        let table = ({ onRowsRendered, registerChild }) => (
            <AutoSizer>
                {({ height, width }) => (
                    <Table
                        className={classNames(classes.table, tableClassName)}
                        height={height - 57}
                        width={width}
                        {...tableProps}
                        rowClassName={this.getRowClassName}
                        gridClassName={classes.grid}
                        ref={registerChild}
                        onRowsRendered={onRowsRendered}
                    >
                        {columns.map(({
                            cellContentRenderer = null, className, dataKey, ...other
                        }, index) => {
                            let renderer;
                            if (cellContentRenderer != null) {
                                renderer = (cellRendererProps) => this.cellRenderer({
                                    cellData: cellContentRenderer(cellRendererProps),
                                    columnIndex: index,
                                });
                            } else {
                                renderer = this.cellRenderer;
                            }

                            return (
                                <Column
                                    key={dataKey}
                                    headerRenderer={(headerProps) => this.headerRenderer({
                                        ...headerProps,
                                        columnIndex: index,
                                    })}
                                    className={classNames(classes.flexContainer, className)}
                                    cellRenderer={renderer}
                                    dataKey={dataKey}
                                    {...other}
                                />
                            );
                        })}
                    </Table>
                )}
            </AutoSizer>
        );
        if (loadMoreRows) {
            table = (
                <InfiniteLoader
                    isRowLoaded={isRowLoaded}
                    loadMoreRows={loadMoreRows}
                    rowCount={infiniteLoaderRowCount}
                >
                    {table}
                </InfiniteLoader>
            );
        } else {
            table = table({});
        }
        return table;
    }
}

MuiVirtualizedTable.propTypes = {
    classes: PropTypes.object.isRequired,
    className: PropTypes.string,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            cellContentRenderer: PropTypes.func,
            dataKey: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
            numeric: PropTypes.bool,
            disableSort: PropTypes.bool,
        }),
    ).isRequired,
    disableHeader: PropTypes.bool,
    headerHeight: PropTypes.number,
    isRowLoaded: PropTypes.func,
    loadMoreRows: PropTypes.func,
    onRowClick: PropTypes.func,
    rowClassName: PropTypes.string,
    infiniteLoaderRowCount: PropTypes.number,
    rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    showRowHover: PropTypes.bool,
    sort: PropTypes.func,
    tableCellStyle: PropTypes.object,
};

MuiVirtualizedTable.defaultProps = {
    disableHeader: true,
    headerHeight: 48,
    infiniteLoaderRowCount: Number.MAX_SAFE_INTEGER,
    rowHeight: 48,
    showRowHover: false,
    tableCellStyle: {},
};

export default withStyles(styles)(MuiVirtualizedTable);
